import { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import { useOneAuth } from '@withone/auth';
import { useQueryClient } from 'react-query';
import { Spinner } from 'components/elements/Spinner';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { AddHubspotContact } from 'components/modals/AddHubspotContact';
import { HUBSPOT_CONTACTS_QUERY_KEY } from 'hooks/useHubspotContacts';
import {
  ONE_AUTH_CONNECTIONS_QUERY_KEY,
  useDeleteOneAuthConnection,
  useOneAuthConnections,
} from 'hooks/useOneAuthConnections';
import { useOneProvisionStatus, useProvisionOne } from 'hooks/useOneProvision';
import { useToast } from 'hooks/useToast';
import { OneAuthConnection } from 'services/API/OneAuthAPI';
import { useUser } from 'state/auth';
import { API_BASE } from 'utils/constants';

// The One Auth widget runs in an iframe on a different origin, so the token
// endpoint must be an absolute URL. API_BASE always ends with a trailing slash.
const ONE_AUTH_TOKEN_URL = `${API_BASE}one-auth/token/`;

// The CRMs a user can connect. `name` must match the One connector display name
// (used to deep-link the One Auth widget). A user connects exactly one.
type CrmOption = {
  platform: string;
  name: string;
  description: string;
  icon: (typeof Icon.Name)[keyof typeof Icon.Name];
};

const CRM_OPTIONS: CrmOption[] = [
  {
    platform: 'hubspot',
    name: 'HubSpot',
    description: 'Sync your HubSpot contacts and manage them from Mercator.',
    icon: Icon.Name.Hubspot,
  },
  {
    platform: 'salesforce',
    name: 'Salesforce',
    description: 'Connect your Salesforce org to keep records in sync.',
    icon: Icon.Name.Salesforce,
  },
  {
    platform: 'pipedrive',
    name: 'Pipedrive',
    description: 'Sync your Pipedrive deals and contacts.',
    icon: Icon.Name.Pipedrive,
  },
  {
    platform: 'project-mark',
    name: 'ProjectMark',
    description: 'Connect ProjectMark to sync your CRM data.',
    icon: Icon.Name.ProjectMark,
  },
];

const styles = {
  intro: {
    color: Colors.TextQuiet,
  },
  options: {
    gap: 'padding_8',
    mt: 'padding_8',
  },
  optionRow: {
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid',
    borderColor: Colors.ArmyGreen200,
    borderRadius: '8px',
    padding: 'padding_16',
    gap: 'padding_16',
  },
  optionInfo: {
    alignItems: 'center',
    gap: 'padding_16',
  },
  optionText: {
    gap: 'padding_4',
  },
  label: {
    color: Colors.TextQuiet,
  },
  connectedRow: {
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid',
    borderColor: Colors.ArmyGreen200,
    borderRadius: '8px',
    padding: 'padding_16',
  },
  contactsHeader: {
    mt: 'padding_8',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
};

const connectionLabel = (connection: OneAuthConnection) =>
  connection.title || connection.name || connection.platform;

const ConnectCrm = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { renderCustomToast: toast } = useToast();

  // One identity is the users-app profile id (what OneProject is keyed on),
  // not the Django auth user id — so connections land in the user's project.
  const identity = user?.profile_id ? String(user.profile_id) : undefined;

  const { data: provisionStatus, isLoading: isLoadingProvision } = useOneProvisionStatus(
    Boolean(identity),
  );
  const provisionMutation = useProvisionOne();
  const isProvisioned = Boolean(provisionStatus?.provisioned);

  const { data: connections = [], isLoading: isLoadingConnections } =
    useOneAuthConnections(isProvisioned ? identity : undefined);

  const deleteConnection = useDeleteOneAuthConnection();

  const [pendingCrm, setPendingCrm] = useState<string | null>(null);
  const [connectionToDelete, setConnectionToDelete] = useState<OneAuthConnection | null>(
    null,
  );
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);

  const { open } = useOneAuth({
    token: {
      url: ONE_AUTH_TOKEN_URL,
      headers: {
        'x-user-id': identity,
      },
    },
    selectedConnection: pendingCrm ?? undefined,
    companyName: 'Mercator',
    onSuccess: () => {
      queryClient.invalidateQueries([ONE_AUTH_CONNECTIONS_QUERY_KEY]);
    },
  });

  // Open the widget only after the chosen CRM has propagated into the hook
  // config, so it deep-links to the right connector.
  useEffect(() => {
    if (!pendingCrm) return;
    open();
    setPendingCrm(null);
  }, [pendingCrm, open]);

  const handleEnable = useCallback(() => {
    provisionMutation.mutate(undefined, {
      onSuccess: () => {
        toast({ title: 'CRM integration enabled', status: 'success' });
        queryClient.invalidateQueries([ONE_AUTH_CONNECTIONS_QUERY_KEY]);
      },
      onError: () => {
        toast({ title: 'Failed to enable CRM integration', status: 'error' });
      },
    });
  }, [provisionMutation, toast, queryClient]);

  const handleConfirmDelete = useCallback(() => {
    if (!connectionToDelete) return;
    deleteConnection.mutate(connectionToDelete.id, {
      onSuccess: () => {
        toast({ title: 'CRM disconnected', status: 'success' });
        queryClient.invalidateQueries([HUBSPOT_CONTACTS_QUERY_KEY]);
        setConnectionToDelete(null);
      },
      onError: () => {
        toast({ title: 'Failed to disconnect CRM', status: 'error' });
      },
    });
  }, [connectionToDelete, deleteConnection, toast, queryClient]);

  const renderEnable = () => (
    <FlexColumn sx={{ gap: 'padding_8' }}>
      <Text.Body sx={styles.intro}>
        Connect a CRM to sync your contacts and deals with Mercator. Enabling sets up a
        secure, private workspace for your account — your data stays isolated from other
        users and your credentials are encrypted. You can connect one CRM.
      </Text.Body>
      <FlexRow>
        <Button
          variant="primary"
          onClick={handleEnable}
          isDisabled={!identity || provisionMutation.isLoading}
          isLoading={provisionMutation.isLoading}
        >
          Enable CRM integration
        </Button>
      </FlexRow>
    </FlexColumn>
  );

  const renderPicker = () => (
    <FlexColumn sx={{ gap: 'padding_8' }}>
      <Text.Body sx={styles.intro}>
        Choose the CRM you’d like to connect. You can connect one CRM — to switch later,
        disconnect it first.
      </Text.Body>
      <FlexColumn sx={styles.options}>
        {CRM_OPTIONS.map((crm) => (
          <FlexRow key={crm.platform} sx={styles.optionRow}>
            <FlexRow sx={styles.optionInfo}>
              <Icon name={crm.icon} size={32} />
              <FlexColumn sx={styles.optionText}>
                <Text.Body>{crm.name}</Text.Body>
                <Text.SmallLabel sx={styles.label}>{crm.description}</Text.SmallLabel>
              </FlexColumn>
            </FlexRow>
            <Button
              variant="secondary"
              onClick={() => setPendingCrm(crm.name)}
              isDisabled={!identity}
            >
              Connect
            </Button>
          </FlexRow>
        ))}
      </FlexColumn>
    </FlexColumn>
  );

  const renderConnected = () => (
    <FlexColumn sx={{ gap: 'padding_16' }}>
      {connections.map((connection) => {
        const crmOption = CRM_OPTIONS.find((crm) => crm.platform === connection.platform);

        return (
          <FlexRow key={connection.key} sx={styles.connectedRow}>
            <FlexRow sx={styles.optionInfo}>
              {crmOption && <Icon name={crmOption.icon} size={32} />}
              <FlexColumn sx={styles.optionText}>
                <Text.Body>{connectionLabel(connection)}</Text.Body>
                <Text.SmallLabel sx={styles.label}>Connected</Text.SmallLabel>
              </FlexColumn>
            </FlexRow>
            <Button
              variant="alert"
              onClick={() => setConnectionToDelete(connection)}
              startAdornment={<Icon name={Icon.Name.Trash} />}
            >
              Disconnect
            </Button>
          </FlexRow>
        );
      })}
    </FlexColumn>
  );

  const renderBody = () => {
    if (isLoadingProvision) {
      return <Spinner />;
    }

    if (!isProvisioned) {
      return renderEnable();
    }

    if (isLoadingConnections) {
      return <Spinner />;
    }

    return connections.length === 0 ? renderPicker() : renderConnected();
  };

  return (
    <FlexColumn sx={{ gap: 'padding_16' }}>
      {renderBody()}

      <AddHubspotContact
        isOpen={isAddContactOpen}
        onClose={() => setIsAddContactOpen(false)}
        identity={identity}
      />

      <Modal
        isOpen={Boolean(connectionToDelete)}
        onClose={() => setConnectionToDelete(null)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Disconnect CRM</ModalHeader>
          <ModalBody>
            <Text.Body>
              Are you sure you want to disconnect{' '}
              {connectionToDelete ? connectionLabel(connectionToDelete) : ''}? This
              permanently removes its stored credentials. You can connect a different CRM
              afterwards.
            </Text.Body>
          </ModalBody>
          <ModalFooter sx={{ gap: 'padding_8' }}>
            <Button
              variant="functional"
              onClick={() => setConnectionToDelete(null)}
              isDisabled={deleteConnection.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="alert"
              onClick={handleConfirmDelete}
              isLoading={deleteConnection.isLoading}
            >
              Disconnect
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </FlexColumn>
  );
};

export default ConnectCrm;
